import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import pdfjs from '../lib/pdfjs';
import { formatDateBritish, todayIso } from '../lib/compliance';
import {
  resolveEditableVersion, createDraftFrom, saveDraftContent, discardDraft,
  publishVersion, countSuppliersOnVersion,
} from '../lib/documentDrafts';
import PublishVersionDialog from '../components/documents/PublishVersionDialog';
import { useCurrentInternalUser } from '../lib/currentUser';

// PDF field-placement tool (brief-pdf-signature-fields §4). Loads the document's
// source PDF, renders every page, and lets an admin drop fields onto the pages,
// move and resize them. Field positions are stored in PDF POINTS with a
// bottom-left origin (field_layout jsonb) so they survive zoom + viewport.
//
// Palette mirrors the prose editor: identity (prefilled, editable), they-fill-in
// (never prefilled) and signature. Publish is gated on a placed signature field
// (§4) and no identity source may sit on a signature (§5, enforced here + on the
// stored layout).

const FONT = "font-['Metropolis']";

// Palette definition. `kind` drives colour + the prefill rule; identity fields
// carry a prefill_source, the rest don't. Default field sizes are in PDF points.
const PALETTE = {
  identity: {
    label: 'From their account · prefilled, editable',
    tone: '#2E8FA1',
    items: [
      { type: 'identity', prefill_source: 'company_name', label: 'Company name', w: 160, h: 16 },
      { type: 'identity', prefill_source: 'company_number', label: 'Company number', w: 110, h: 16 },
      { type: 'identity', prefill_source: 'vat_number', label: 'VAT number', w: 120, h: 16 },
      { type: 'identity', prefill_source: 'registered_address', label: 'Registered address', w: 200, h: 32 },
    ],
  },
  fill: {
    label: 'They fill in · never prefilled',
    tone: '#2F6E14',
    items: [
      { type: 'text', label: 'Full name', w: 160, h: 16 },
      { type: 'text', label: 'Company', w: 170, h: 16 },
      { type: 'text', label: 'Address', w: 200, h: 32 },
      { type: 'text', label: 'Role', w: 130, h: 16 },
      { type: 'text', label: 'Email', w: 170, h: 16 },
      { type: 'date', label: 'Date', w: 90, h: 16 },
      { type: 'text', label: 'Short text', w: 150, h: 16 },
      { type: 'tick', label: 'Tick box', w: 16, h: 16 },
    ],
  },
  signature: {
    label: 'Signature',
    tone: '#131313',
    items: [
      { type: 'signature', label: 'Signature', w: 180, h: 46 },
      { type: 'date_signed', label: 'Date signed', w: 90, h: 16 },
    ],
  },
};

let _fid = 0;
const newFieldId = () => `f${Date.now().toString(36)}_${(_fid++).toString(36)}`;

const fieldColour = (f) =>
  f.type === 'signature' || f.type === 'date_signed' ? '#131313'
    : f.type === 'identity' ? '#2E8FA1'
      : '#2F6E14';

const todayLabel = formatDateBritish(todayIso());

export default function PdfFieldEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  // The live published version, when there is one — `doc` is either a draft of
  // it or (before the first save) the live row itself.
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [affectedCount, setAffectedCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);
  const { user: internalUser } = useCurrentInternalUser();

  // fields: [{ id, type, prefill_source, label, page(1-based), x, y, w, h, required }] in PDF points.
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Alignment guides shown mid-drag: page-local screen-px lines the dragged
  // field has snapped to. null when nothing is snapping.
  const [guides, setGuides] = useState(null);
  // Preview mode — renders the placed fields exactly as the supplier sees them
  // (read-only, no drag handles) so an author can sanity-check placement.
  const [preview, setPreview] = useState(false);

  // Per-page render metadata for the point<->pixel conversion.
  const pagesRef = useRef([]); // [{ scale, widthPx, heightPx, el }]
  const pageWrapRef = useRef(null);
  const scrollRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [numPages, setNumPages] = useState(0);
  // The page currently in view — a new field drops onto THIS page, so an author
  // can scroll to (say) the last page and place a signature there.
  const [currentPage, setCurrentPage] = useState(1);

  // ---- Load the document ----
  useEffect(() => {
    let alive = true;
    (async () => {
      // Same rule as the prose editor (document-versioning §3a): editing a
      // PUBLISHED document edits a DRAFT of it — moving a signature field on a
      // document suppliers have already signed would change what they signed.
      const { row: data, live: liveRow, error: err } = await resolveEditableVersion(docId);
      if (!alive) return;
      if (err || !data) { setError(err || 'Document not found.'); setLoading(false); return; }
      if (data.satisfied_by !== 'pdf_sign') { setError('This document isn’t a PDF-sign document.'); setLoading(false); return; }
      if (!data.source_pdf_path) { setError('This document has no source PDF.'); setLoading(false); return; }
      setDoc(data);
      setLive(liveRow || null);
      setFields(Array.isArray(data.field_layout) ? data.field_layout.map(f => ({ ...f, id: f.id || newFieldId() })) : []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [docId]);

  // ---- Render the PDF pages ----
  useEffect(() => {
    if (!doc?.source_pdf_path || !pageWrapRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const wrap = pageWrapRef.current;
        wrap.innerHTML = '';
        pagesRef.current = [];
        const loadingTask = pdfjs.getDocument(doc.source_pdf_path);
        const pdf = await loadingTask.promise;
        setNumPages(pdf.numPages);
        // Track the page most in view — a new field drops onto it.
        const io = new IntersectionObserver((ents) => {
          let best = null;
          ents.forEach(e => { if (!best || e.intersectionRatio > best.intersectionRatio) best = e; });
          if (best && best.isIntersecting) setCurrentPage(Number(best.target.getAttribute('data-page')) || 1);
        }, { root: scrollRef.current || null, threshold: [0.25, 0.5, 0.75] });
        const targetWidth = Math.min(760, wrap.clientWidth || 760);
        for (let i = 1; i <= pdf.numPages; i += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const unscaled = page.getViewport({ scale: 1 });
          const scale = targetWidth / unscaled.width;
          const dpr = Math.min(window.devicePixelRatio || 1, 2); // crisp on retina, capped so canvases stay sane
          const viewport = page.getViewport({ scale });            // CSS/display size
          const hi = page.getViewport({ scale: scale * dpr });     // hi-res backing store
          const w = Math.round(viewport.width), h = Math.round(viewport.height);
          const pageBox = document.createElement('div');
          pageBox.style.position = 'relative';
          pageBox.style.margin = '0 auto 18px';
          pageBox.style.width = `${w}px`;
          pageBox.style.height = `${h}px`;
          pageBox.style.boxShadow = '0 2px 12px rgba(45,48,35,0.12)';
          pageBox.setAttribute('data-page', String(i));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(hi.width);
          canvas.height = Math.round(hi.height);
          canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
          canvas.style.display = 'block';
          pageBox.appendChild(canvas);
          wrap.appendChild(pageBox);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: hi }).promise;
          pagesRef.current[i] = { scale, widthPx: w, heightPx: h, el: pageBox };
          io.observe(pageBox);
        }
        if (!cancelled) setRendered(true);
      } catch (e) {
        if (!cancelled) setError(`Could not render the PDF: ${e?.message || e}`);
      }
    })();
    return () => { cancelled = true; };
  }, [doc?.source_pdf_path]);

  // PDF points -> screen box {left, top, width, height} for a page.
  const toScreen = useCallback((f) => {
    const p = pagesRef.current[f.page];
    if (!p) return null;
    return {
      left: f.x * p.scale,
      top: p.heightPx - (f.y + f.h) * p.scale,
      width: f.w * p.scale,
      height: f.h * p.scale,
    };
  }, []);

  // screen box -> PDF points for a page.
  const toPoints = useCallback((page, box) => {
    const p = pagesRef.current[page];
    if (!p) return null;
    return {
      x: box.left / p.scale,
      y: (p.heightPx - (box.top + box.height)) / p.scale,
      w: box.width / p.scale,
      h: box.height / p.scale,
    };
  }, []);

  const patchField = useCallback((id, patch) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
    setSavedAt(null);
  }, []);

  const addField = useCallback((item) => {
    if (preview) return; // no editing while previewing
    // Drop onto the page currently in view, near its top-left; the author
    // drags it into place from there.
    const page = pagesRef.current[currentPage] ? currentPage : 1;
    const p = pagesRef.current[page];
    if (!p) { setError('The PDF is still loading — try again in a moment.'); setTimeout(() => setError(null), 2500); return; }
    const startBox = { left: 40, top: 40, width: item.w * p.scale, height: item.h * p.scale };
    const pts = toPoints(page, startBox);
    const field = {
      id: newFieldId(),
      type: item.type,
      prefill_source: item.prefill_source || null,
      label: item.label,
      page,
      required: item.type === 'signature',
      ...pts,
    };
    setFields(prev => [...prev, field]);
    setSelectedId(field.id);
    setSavedAt(null);
  }, [toPoints, currentPage, preview]);

  const removeField = useCallback((id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    setSelectedId(s => (s === id ? null : s));
    setSavedAt(null);
  }, []);

  // ---- Drag + resize (pointer) ----
  const dragRef = useRef(null);
  const onFieldPointerDown = (e, f, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(f.id);
    const box = toScreen(f);
    if (!box) return;
    dragRef.current = { id: f.id, page: f.page, mode, startX: e.clientX, startY: e.clientY, box: { ...box } };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const p = pagesRef.current[d.page];
    if (!p) return;
    let box;
    if (d.mode === 'move') {
      box = { left: clamp(d.box.left + dx, 0, p.widthPx - d.box.width), top: clamp(d.box.top + dy, 0, p.heightPx - d.box.height), width: d.box.width, height: d.box.height };
    } else {
      box = { left: d.box.left, top: d.box.top, width: Math.max(14, Math.min(d.box.width + dx, p.widthPx - d.box.left)), height: Math.max(12, Math.min(d.box.height + dy, p.heightPx - d.box.top)) };
    }
    // Snap to other fields' edges/centres + the page centre, and record the
    // guide lines to draw. Shift is held → snap off, for fine placement.
    const g = e.shiftKey ? null : snapBox(d.page, box, d.mode, d.id);
    setGuides(g);
    const pts = toPoints(d.page, box);
    setFields(prev => prev.map(f => f.id === d.id ? { ...f, ...pts } : f));
  };

  // Snap the dragged box's edges/centres to nearby anchors and return the
  // guide lines to render. Mutates `box` in place. Anchors: every other field
  // on the page (left/centre/right, top/middle/bottom) plus the page centre.
  const SNAP = 6; // px tolerance
  const snapBox = (page, box, mode, dragId) => {
    const p = pagesRef.current[page];
    if (!p) return null;
    const vx = [p.widthPx / 2];   // vertical guide candidates (x positions)
    const hy = [p.heightPx / 2];  // horizontal guide candidates (y positions)
    fields.forEach(f => {
      if (f.page !== page || f.id === dragId) return;
      const o = toScreen(f);
      if (!o) return;
      vx.push(o.left, o.left + o.width / 2, o.left + o.width);
      hy.push(o.top, o.top + o.height / 2, o.top + o.height);
    });
    const nearest = (anchors, targets) => {
      let best = null;
      anchors.forEach(a => targets.forEach(t => {
        const dist = t - a;
        if (Math.abs(dist) <= SNAP && (best === null || Math.abs(dist) < Math.abs(best.dist))) best = { dist, at: t };
      }));
      return best;
    };
    const guideV = [], guideH = [];
    if (mode === 'move') {
      const sx = nearest([box.left, box.left + box.width / 2, box.left + box.width], vx);
      if (sx) { box.left += sx.dist; guideV.push(sx.at); }
      const sy = nearest([box.top, box.top + box.height / 2, box.top + box.height], hy);
      if (sy) { box.top += sy.dist; guideH.push(sy.at); }
    } else { // resize: snap the right + bottom edges only
      const sx = nearest([box.left + box.width], vx);
      if (sx) { box.width += sx.dist; guideV.push(sx.at); }
      const sy = nearest([box.top + box.height], hy);
      if (sy) { box.height += sy.dist; guideH.push(sy.at); }
    }
    return guideV.length || guideH.length ? { page, vx: guideV, hy: guideH } : null;
  };

  const onPointerUp = () => {
    dragRef.current = null;
    setGuides(null);
    window.removeEventListener('pointermove', onPointerMove);
    setSavedAt(null);
  };

  // ---- Problems (publish gate) ----
  const hasSignature = fields.some(f => f.type === 'signature');
  const badPrefill = fields.some(f => (f.type === 'signature' || f.type === 'date_signed') && f.prefill_source);
  const problems = [];
  if (!hasSignature) problems.push('Place a signature field before publishing.');
  if (badPrefill) problems.push('A signature field can’t be prefilled — remove the prefill source.');

  // Stable field ids are kept — they key the supplier's answers.
  const layoutOf = useCallback(() => fields.map(f => ({
    id: f.id, type: f.type, prefill_source: f.prefill_source || null, label: f.label || '',
    page: f.page, x: round(f.x), y: round(f.y), w: round(f.w), h: round(f.h), required: !!f.required,
  })), [fields]);

  const saveDraft = useCallback(async () => {
    if (!doc || busy) return;
    setBusy('draft'); setError(null);
    try {
      const content = { field_layout: layoutOf() };
      if (doc.status === 'active') {
        // Editing a published PDF document starts a draft of it; the live one
        // keeps being the document suppliers sign until this is published.
        const draft = await createDraftFrom(doc, content);
        setLive(doc);
        setDoc(draft);
      } else {
        await saveDraftContent(doc, content);
      }
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not save the draft.');
    }
    setBusy(null);
  }, [doc, busy, layoutOf]);

  const discardWorkingDraft = useCallback(async () => {
    if (!live || !doc || doc.id === live.id || busy) return;
    setBusy('discard'); setError(null);
    try {
      await discardDraft(doc.id);
      setDoc(live);
      setFields(Array.isArray(live.field_layout) ? live.field_layout.map(f => ({ ...f, id: f.id || newFieldId() })) : []);
      setSavedAt(null);
    } catch (e) {
      setError(e.message || 'Could not discard the draft.');
    }
    setBusy(null);
  }, [live, doc, busy]);

  const runPublish = useCallback(async (choice) => {
    if (!doc) return;
    setBusy('publish'); setError(null);
    try {
      const content = { field_layout: layoutOf() };
      let draft = doc;
      if (live && doc.id === live.id) draft = await createDraftFrom(live, content);
      // The content hash for a pdf_sign document covers the SOURCE PDF BYTES
      // plus the placed fields (§7), so fetch the bytes to hash them. Best
      // effort: a fetch failure leaves the hash null (recorded as "unknown"
      // later) rather than blocking a publish.
      let pdfBytes = null;
      try {
        if (doc.source_pdf_path) pdfBytes = await fetch(doc.source_pdf_path).then(r => (r.ok ? r.arrayBuffer() : null));
      } catch { /* hash stays null */ }
      const result = await publishVersion({
        draft, live, content, pdfBytes,
        publishedByUserId: internalUser?.id || null,
        choice,
      });
      // Published either way — but don't walk away implying every affected
      // supplier was told when some emails failed.
      if (result?.notified?.failed) {
        setError(`Published — but ${result.notified.failed} supplier ${result.notified.failed === 1 ? 'email' : 'emails'} couldn't be sent (no contact address, or the mail service refused). Chase them from the document's version history.`);
        setBusy(null);
        setPublishOpen(false);
        return;
      }
      navigate('/documents');
    } catch (e) {
      setError(e.message || 'Could not publish.');
      setBusy(null);
      setPublishOpen(false);
    }
  }, [doc, live, layoutOf, internalUser, navigate]);

  const startPublish = useCallback(async () => {
    if (busy || !doc || problems.length > 0) return;
    if (!live) { await runPublish(null); return; }
    setPublishOpen(true);
    setCountLoading(true);
    const n = await countSuppliersOnVersion(live);
    setAffectedCount(n);
    setCountLoading(false);
    // problems is recomputed every render (plain array), so it can't be a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, doc, live, runPublish]);

  if (loading) return <Shell><p className={`${FONT} text-sm text-fusion-mid-grey p-8`}>Loading…</p></Shell>;
  if (error && !doc) return <Shell><p className={`${FONT} text-sm text-[#E0290A] p-8`}>{error}</p></Shell>;

  const selected = fields.find(f => f.id === selectedId) || null;

  return (
    <Shell>
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-line flex-wrap">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate('/documents')} className={`${FONT} text-[11px] font-bold uppercase tracking-[0.16em] text-[#51534A] hover:text-fusion-mid-grey`}>← Fusion Documents</button>
          <div className={`${FONT} text-lg font-bold text-ink truncate`}>{doc.document_name}
            <span className={`${FONT} ml-2 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-md ${live && doc.id === live.id ? 'text-fusion-forest bg-[#92D400]/15' : 'text-[#7A4E10] bg-[#E89A2E]/15'}`}>
              {live
                ? (doc.id === live.id ? `Published · v${live.version_value || 1}` : `Draft of v${doc.version_value}`)
                : 'Draft'}
            </span>
            <span className={`${FONT} ml-2 text-[11px] text-[#51534A]`}>
              {live
                ? `Suppliers keep signing v${live.version_value || 1} until you publish.`
                : 'Place the fields suppliers complete, then publish.'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {savedAt && <span className={`${FONT} text-[11px] text-[#51534A]`}>Saved</span>}
          <button type="button" onClick={() => { setSelectedId(null); setPreview(p => !p); }}
            className={`${FONT} rounded-xl text-xs font-semibold px-4 py-2 border transition-colors ${preview ? 'bg-[#2E8FA1] border-[#2E8FA1] text-white hover:brightness-110' : 'bg-white border-[#2E8FA1]/40 text-[#2E8FA1] hover:border-[#2E8FA1]'}`}>
            {preview ? 'Exit preview' : 'Preview document'}
          </button>
          {live && doc.id !== live.id && (
            <button type="button" onClick={discardWorkingDraft} disabled={!!busy}
              className={`${FONT} bg-white border border-line rounded-xl text-[#51534A] text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] disabled:opacity-50`}>
              {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
            </button>
          )}
          <button type="button" onClick={saveDraft} disabled={!!busy}
            className={`${FONT} bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] disabled:opacity-50`}>
            {busy === 'draft' ? 'Saving…' : 'Save draft'}
          </button>
          <button type="button" onClick={startPublish} disabled={!!busy || problems.length > 0}
            title={problems.length > 0 ? problems.join(' ') : undefined}
            className={`${FONT} rounded-xl text-xs font-bold px-4 py-2 ${problems.length > 0 ? 'bg-[#DADCCD] text-white cursor-not-allowed' : 'bg-[#92D400] text-[#131313] hover:bg-[#a4e810]'}`}>
            {busy === 'publish' ? 'Publishing…' : problems.length > 0 ? `Publish — ${problems.length} to fix` : (live ? `Publish v${(Number(live.version_value) || 1) + 1}` : 'Publish')}
          </button>
        </div>
      </div>

      {error && doc && <div className={`${FONT} text-[12px] text-[#E0290A] px-6 py-2 bg-[#E0290A]/[0.05]`}>{error}</div>}

      <div className="flex-1 flex min-h-0">
        {/* Pages */}
        <div ref={scrollRef} className="flex-1 overflow-auto bg-[#f3f5ee] p-6">
          {preview && (
            <div className={`${FONT} max-w-[760px] mx-auto mb-4 flex items-center gap-2 text-[12px] text-[#14655A] bg-[#E4F0F4] border border-[#C4DEE6] rounded-lg px-3 py-2`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview — this is how the fields appear to the supplier. Editing is paused; exit preview to make changes.
            </div>
          )}
          <div ref={pageWrapRef} className="relative">
            {/* pdf.js canvases are injected here; field overlays portal into each
                page box (position:relative) so their coordinates line up. */}
          </div>
          {rendered && fields.map(f => {
            const p = pagesRef.current[f.page];
            const box = toScreen(f);
            if (!p?.el || !box) return null;
            return createPortal(
              preview
                ? <FieldPreview key={f.id} f={f} box={box} todayLabel={todayLabel} />
                : <FieldBox key={f.id} f={f} box={box} colour={fieldColour(f)} selected={f.id === selectedId}
                    onDown={onFieldPointerDown} onSelect={setSelectedId} />,
              p.el
            );
          })}
          {rendered && guides && pagesRef.current[guides.page]?.el &&
            createPortal(<GuideLines guides={guides} />, pagesRef.current[guides.page].el)}
        </div>

        {/* Palette + inspector */}
        <div className={`w-72 flex-shrink-0 border-l border-line bg-white overflow-auto p-4 flex flex-col gap-4 ${preview ? 'opacity-40 pointer-events-none' : ''}`} aria-hidden={preview}>
          {Object.entries(PALETTE).map(([key, group]) => (
            <div key={key} className="bg-white border border-line rounded-xl p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#9BA08C] mb-1.5">{group.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {group.items.map((it, i) => (
                  <button key={i} type="button" onClick={() => addField(it)}
                    className={`${FONT} text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors`}
                    style={{ color: group.tone, borderColor: `${group.tone}55`, background: `${group.tone}14` }}>
                    + {it.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Inspector for the selected field */}
          {selected && (
            <div className="bg-[#EEF1E6] border border-line rounded-xl p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-1.5">Selected field</div>
              <div className={`${FONT} text-[13px] font-semibold text-ink mb-2`}>{selected.label}</div>
              <label className={`${FONT} flex items-center gap-2 text-[12px] text-fusion-mid-grey mb-2`}>
                <input type="checkbox" checked={!!selected.required} onChange={e => patchField(selected.id, { required: e.target.checked })} className="accent-[#92D400]" />
                Required
              </label>
              <label className={`${FONT} flex items-center gap-2 text-[12px] text-fusion-mid-grey mb-2`}>
                Page
                <select value={selected.page} onChange={e => patchField(selected.id, { page: Number(e.target.value) })}
                  className={`${FONT} bg-white border border-line rounded px-2 py-0.5 text-[12px]`}>
                  {Array.from({ length: numPages }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <p className={`${FONT} text-[11px] text-[#51534A] mb-2`}>Drag to move, drag the corner to resize. Fields snap to line up with each other — hold Shift to place freely.</p>
              <button type="button" onClick={() => removeField(selected.id)}
                className={`${FONT} text-[11px] font-semibold text-[#E0290A] hover:underline`}>Remove field</button>
            </div>
          )}

          {/* Publish gate */}
          <div className={`rounded-xl p-3 border ${problems.length > 0 ? 'bg-[#E0290A]/[0.04] border-[#E0290A]/30' : 'bg-[#92D400]/[0.05] border-[#92D400]/30'}`}>
            <div className={`text-[10px] font-bold uppercase tracking-[0.16em] mb-1 ${problems.length > 0 ? 'text-[#9c2b12]' : 'text-fusion-forest'}`}>
              {problems.length > 0 ? `${problems.length} to fix` : 'Ready to publish'}
            </div>
            {problems.length > 0
              ? <ul className={`${FONT} text-[12px] text-[#51534A] list-disc pl-4`}>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
              : <p className={`${FONT} text-[12px] text-[#51534A]`}>A signature field is placed. Publishing makes this the live document suppliers sign.</p>}
          </div>
        </div>
      </div>

      <PublishVersionDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onConfirm={(choice) => runPublish(choice)}
        documentName={doc.document_name || 'Untitled document'}
        liveVersion={live?.version_value || 1}
        nextVersion={live && doc.id !== live.id ? doc.version_value : (Number(live?.version_value) || 1) + 1}
        affectedCount={affectedCount}
        countLoading={countLoading}
        busy={busy === 'publish'}
        error={error}
      />
    </Shell>
  );
}

// A single field box, portalled into its page box (which is position:relative),
// so left/top are the page-local screen coordinates from toScreen().
function FieldBox({ f, box, colour, selected, onDown, onSelect }) {
  return (
    <div
      onPointerDown={(e) => onDown(e, f, 'move')}
      onClick={(e) => { e.stopPropagation(); onSelect(f.id); }}
      className="absolute flex items-center justify-center select-none"
      style={{
        left: box.left, top: box.top, width: box.width, height: box.height,
        border: `1.5px solid ${colour}`, background: `${colour}1f`, borderRadius: 3, cursor: 'move',
        boxShadow: selected ? `0 0 0 2px ${colour}66` : 'none', zIndex: selected ? 20 : 10,
      }}>
      <span className="font-['Metropolis'] text-[9px] font-bold truncate px-1 pointer-events-none" style={{ color: colour }}>
        {f.type === 'signature' ? '✍ Signature' : f.type === 'tick' ? '☐' : f.label}
      </span>
      <span
        onPointerDown={(e) => onDown(e, f, 'resize')}
        className="absolute"
        style={{ right: -5, bottom: -5, width: 11, height: 11, background: colour, borderRadius: 2, cursor: 'nwse-resize', zIndex: 21 }} />
    </div>
  );
}

// Magenta alignment guides drawn across the page while a field is snapping,
// portalled into the page box (position:relative). Purely visual — no pointer
// events so they never interfere with the drag underneath.
function GuideLines({ guides }) {
  const colour = '#E5006D';
  return (
    <>
      {guides.vx.map((x, i) => (
        <div key={`v${i}`} className="absolute pointer-events-none"
          style={{ left: Math.round(x), top: 0, width: 1, height: '100%', background: colour, zIndex: 30 }} />
      ))}
      {guides.hy.map((y, i) => (
        <div key={`h${i}`} className="absolute pointer-events-none"
          style={{ left: 0, top: Math.round(y), height: 1, width: '100%', background: colour, zIndex: 30 }} />
      ))}
    </>
  );
}

// Read-only render of a field as the supplier sees it, for Preview mode.
// Kept visually in step with PdfSignModal's PdfField: neutral-until-filled,
// dashed prefilled/auto fields, a required marker on empty required fields.
function FieldPreview({ f, box, todayLabel }) {
  const c = fieldColour(f);
  const common = { position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height };
  const NEUTRAL = '#C3C9AF';
  const badge = f.required ? (
    <span className="absolute font-['Metropolis'] text-[10px] font-bold" style={{ left: box.left + box.width - 4, top: box.top - 7, color: '#D8341A', zIndex: 22 }}>*</span>
  ) : null;
  if (f.type === 'signature') {
    return (<>
      <div style={{ ...common, border: `1.5px dashed ${c}`, background: `${c}0d`, borderRadius: 3 }}
        className="flex items-center justify-center">
        <span className="font-['Metropolis'] text-[10px] font-bold" style={{ color: c }}>✍ Sign</span>
      </div>{badge}
    </>);
  }
  if (f.type === 'date_signed') {
    return <div style={{ ...common, border: `1px dashed ${NEUTRAL}`, background: '#F4F6EA', display: 'flex', alignItems: 'center', padding: '0 3px', borderRadius: 2 }}
      className="font-['Metropolis'] text-[10px] text-[#6B7566]">{todayLabel}</div>;
  }
  if (f.type === 'tick') {
    return (<>
      <div style={{ ...common, border: `1.5px solid ${NEUTRAL}`, background: '#fff', borderRadius: 3 }} />{badge}
    </>);
  }
  const prefilled = f.type === 'identity';
  return (<>
    <div style={{ ...common, border: `1px ${prefilled ? 'dashed' : 'solid'} ${prefilled ? NEUTRAL : NEUTRAL}`, background: prefilled ? '#F4F6EA' : '#fff', borderRadius: 2, padding: '0 4px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}
      className={`font-['Metropolis'] text-[10px] ${prefilled ? 'text-[#6B7566]' : 'text-[#B4BCA8]'}`}>
      {f.label}
    </div>{badge}
  </>);
}

function Shell({ children }) {
  // h-screen (not min-h-screen) so the page area scrolls INSIDE the panel and
  // the top bar + right-hand palette stay fixed instead of scrolling away.
  return <div className="h-screen overflow-hidden bg-canvas flex flex-col">{children}</div>;
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round = (n) => Math.round((Number(n) || 0) * 100) / 100;
